/**
 * Mapping des erreurs d'authentification Supabase → messages FR pour l'UI.
 * Supabase renvoie des messages/codes en anglais ; on les traduit. On matche
 * d'abord sur `error.code` (stable, supabase-js v2), puis sur le message en
 * fallback. Tout inconnu → message générique FR.
 */
type SupabaseAuthError = {
  code?: string;
  message?: string;
  status?: number;
};

const BY_CODE: Record<string, string> = {
  invalid_credentials: "Email ou mot de passe incorrect.",
  email_not_confirmed: "Votre email n'est pas encore confirmé. Vérifiez votre boîte de réception.",
  user_already_exists: "Cet email est déjà utilisé. Connectez-vous.",
  email_exists: "Cet email est déjà utilisé. Connectez-vous.",
  weak_password: "Mot de passe trop faible (8 caractères minimum).",
  over_request_rate_limit: "Trop de tentatives. Réessayez dans quelques minutes.",
  over_email_send_rate_limit: "Trop d'emails envoyés. Réessayez dans quelques minutes.",
  validation_failed: "Adresse email invalide.",
  signup_disabled: "Les inscriptions sont temporairement fermées.",
  user_not_found: "Aucun compte ne correspond à cet email.",
  same_password: "Le nouveau mot de passe doit être différent de l'ancien.",
};

// Fallback par sous-chaîne du message anglais (anciens SDK / erreurs sans code).
const BY_MESSAGE: Array<[RegExp, string]> = [
  [/invalid login credentials/i, "Email ou mot de passe incorrect."],
  [/email not confirmed/i, "Votre email n'est pas encore confirmé. Vérifiez votre boîte de réception."],
  [/already registered|already exists|user already/i, "Cet email est déjà utilisé. Connectez-vous."],
  [/password should be at least|weak password/i, "Mot de passe trop faible (8 caractères minimum)."],
  [/rate limit|too many requests/i, "Trop de tentatives. Réessayez dans quelques minutes."],
  [/invalid format|unable to validate email/i, "Adresse email invalide."],
  [/network|fetch|failed to fetch/i, "Connexion impossible. Vérifiez votre réseau."],
];

/**
 * LA LIMITE DE 60 SECONDES — Supabase refuse deux emails à la MÊME adresse en moins d'une minute,
 * indépendamment du quota horaire.
 *
 * ⚠️ POURQUOI ELLE MÉRITE SON PROPRE PRÉDICAT. Traitée comme un quota ordinaire, elle produirait
 * « Trop d'emails envoyés, réessayez dans quelques minutes » — vrai sur le principe, faux sur le
 * délai, et surtout ALARMANT pour quelqu'un qui vient simplement de cliquer deux fois. Ce n'est pas
 * une sanction : c'est une seconde d'attente, et l'écran doit le dire comme tel.
 */
export function estLimiteUneMinute(error: unknown): boolean {
  const e = error as SupabaseAuthError | null;
  if (!e) return false;
  if (e.code === "over_email_send_rate_limit") return true;
  return typeof e.message === "string" && /after \d+ seconds?|only request this after/i.test(e.message);
}

/**
 * Les secondes restantes annoncées par le message, ou 60 à défaut.
 *
 * ⚠️ Le repli est VOLONTAIREMENT LE PLAFOND : si le message change de forme ou de langue, on fait
 * patienter une minute pleine plutôt que d'annoncer une durée qu'on n'a pas lue. Une attente trop
 * longue déçoit ; une attente trop courte renvoie l'utilisateur dans le même mur.
 */
export function secondesAvantNouvelEnvoi(error: unknown): number {
  const m = (error as SupabaseAuthError | null)?.message?.match(/after (\d+) seconds?/i);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 && n <= 60 ? n : 60;
}

/** True si l'erreur correspond à « email non confirmé » (pour proposer le renvoi). */
export function isEmailNotConfirmed(error: unknown): boolean {
  const e = error as SupabaseAuthError | null;
  if (!e) return false;
  if (e.code === "email_not_confirmed") return true;
  return typeof e.message === "string" && /email not confirmed/i.test(e.message);
}

/** Traduit une erreur d'auth Supabase en message FR pour l'utilisateur. */
export function frenchAuthError(error: unknown): string {
  const e = error as SupabaseAuthError | null;
  if (e?.code && BY_CODE[e.code]) return BY_CODE[e.code];
  if (typeof e?.message === "string") {
    for (const [re, fr] of BY_MESSAGE) {
      if (re.test(e.message)) return fr;
    }
  }
  return "Une erreur est survenue. Veuillez réessayer.";
}
